import type { AppLocale } from "../i18n/messages";

/** A name kept in both languages, English always present. */
export interface LocalizedName {
  readonly en: string;
  readonly zh?: string | undefined;
}

/** The name in the reader's language, falling back to English. */
export function localName(name: LocalizedName | null | undefined, locale: AppLocale): string {
  if (name === null || name === undefined) return "";
  return (locale === "zh" ? name.zh : undefined) ?? name.en;
}

/** A date and time in the reader's language and time zone. */
/**
 * The locale dates are written in (docs/design/meridian.md, Copy): British English in English, so a date reads
 * "23 Oct 2026, 13:05" as across the product, never the US "Oct 23"; Chinese as it is.
 */
const dateLocale = (locale: AppLocale) => (locale === "zh" ? "zh-CN" : "en-GB");

export function formatWhen(iso: string, locale: AppLocale): string {
  return new Intl.DateTimeFormat(dateLocale(locale), { dateStyle: "medium", timeStyle: "short" }).format(
    new Date(iso),
  );
}

/** A file's size in the reader's language: kilobytes below a megabyte, megabytes with one decimal above. */
export function formatSize(bytes: number, locale: AppLocale): string {
  const megabytes = bytes / 1_000_000;
  return megabytes < 1
    ? new Intl.NumberFormat(locale, { style: "unit", unit: "kilobyte", maximumFractionDigits: 0 }).format(
        Math.max(1, Math.round(bytes / 1_000)),
      )
    : new Intl.NumberFormat(locale, { style: "unit", unit: "megabyte", maximumFractionDigits: 1 }).format(
        megabytes,
      );
}

/** A calendar day (`2026-12-01`) in the reader's language, the same day wherever they are. */
export function formatDay(day: string, locale: AppLocale): string {
  return new Intl.DateTimeFormat(dateLocale(locale), { dateStyle: "medium", timeZone: "UTC" }).format(
    new Date(`${day}T00:00:00Z`),
  );
}

/** A country's name in the reader's language, or its code where the runtime has no name. */
export function countryName(code: string, locale: AppLocale): string {
  return new Intl.DisplayNames([locale], { type: "region" }).of(code) ?? code;
}
